import type { TActivityAggType } from '@/components/sitePerformance/types';

import type {
  TCreateStation,
  TFilterByLocation,
  TGetStationDetails,
  TGetPartnerEvSalesParams,
  TGetStationEvSalesResponse,
  TGetStations,
  TUpdateStation,
  TUploadStationLogo,
  TFilterByStation,
} from '@/types/station';

import { imageService, partnerService, stationService, stationsSearchService } from '@/api/private';

import { toIsoEnd, toIsoStart } from '@/contexts/PortalFilterProvider/portalFilterUtils';

import { toActivityMetricsSiteParams, toStationIdFilterParam } from './stationFilterParam';

export const createStation: TCreateStation = (data) => stationService.post('', data);

export const getStationDetails: TGetStationDetails = (id: string, signal?) =>
  stationService.get(`/${id}`, { signal });

export const updateStation: TUpdateStation = ({ id, patch }) =>
  stationService.patch(`/${id}`, patch);

export const getStations: TGetStations = ({ filter, ...rest }, signal?) => {
  const params = { filter, ...rest };

  if (filter.location?.locationIds) {
    const locationIds = filter.location.locationIds.map((id) => id.split(',')).flat();

    params.filter = { ...filter };
    params.filter.location = { locationIds };
  }

  return stationService.get('', { params, signal });
};

export const filterByLocation: TFilterByLocation = (query, signal?) =>
  stationsSearchService.get('search', { signal, params: { context: 'LOCATION', query } });

export const filterByStation: TFilterByStation = (query, signal?) =>
  stationsSearchService.get('search', {
    signal,
    params: {
      context: 'STATION',
      query: query.length > 1 ? query : undefined,
    },
  });

export const uploadStationLogo: TUploadStationLogo = ({ image, ...data }) =>
  imageService.post(
    'STATIONLOGO',
    {
      image,
      data: new Blob([JSON.stringify(data)], { type: 'application/json' }),
    },
    {
      params: {
        imgType: 'FULL',
      },
    },
  );

/**
 * EV activity metrics (sessions, customers, kWh) per period.
 *
 * Pass `stationIds` for several sites (Dashboard), or the single `stationId` as before
 * (EV Performance). A site list, one site or many, goes as `filter={"stationId":[...]}`; a single
 * `stationId` goes as the legacy `stationId`. The `filter` form needs the backend fix (see
 * `toActivityMetricsSiteParams`).
 *
 * `aggType` is the EV Performance "Group by": one row per day, week (Monday start), month or year.
 * Sent only when given; the backend defaults to DAILY, so the Dashboard chart sends none.
 */
export const getStationActivityMetrics = (
  {
    stationId,
    stationIds,
    ...params
  }: {
    periodType: string;
    startDate: string;
    endDate?: string;
    stationId?: string;
    stationIds?: string[];
    timezoneOffset: number;
    sortBy?: string;
    sort?: string;
    aggType?: TActivityAggType;
  },
  signal?: AbortSignal,
) =>
  stationService.get('activity-metrics', {
    params: { ...params, ...toActivityMetricsSiteParams(stationId, stationIds) },
    signal,
  });

/**
 * EV sales total across many sites: GET /core/partner/ev-sales.
 *
 * What: returns `{ data: { transactionCount, totalAmount } }` for the picked sites (the shape the
 * old per-site `/station/{id}/ev-sales` call also returned). Parse it with `parseEvSalesSummary`.
 * Why: the Dashboard EV Sales card needs one total for all picked sites in one call.
 * Constraints: callers pass `YYYY-MM-DD`; it is sent as ISO-8601 instants (browser-zone day start and
 * end, like the transactions filter), and `to` must be on or
 * after `from` or the server answers 400. Sites go in `filter={"stationId":[...]}`. No ids means
 * every station of the partner (the param is omitted). The server drops ids the partner cannot
 * access, so a total never includes another partner's sites. `signal` lets React Query cancel it.
 */
export const getPartnerEvSales = (
  { stationIds, from, to }: TGetPartnerEvSalesParams,
  signal?: AbortSignal,
) =>
  partnerService.get<TGetStationEvSalesResponse>('/ev-sales', {
    params: {
      from: toIsoStart(from),
      to: toIsoEnd(to),
      ...toStationIdFilterParam(stationIds),
    },
    signal,
  });
